/**
 * Build a ReportTableDoc for the trading product price list.
 *
 * Two granularities:
 *  - 'lot':     one line per FIFO stock lot, grouped per product with a per-product
 *               subtotal and a grand total of line values.
 *  - 'product': one line per product (its current FIFO lot's cost/price).
 *
 * FX policy — "freeze the cost, float the display":
 *  - Each lot's cost is converted into the product's BASE currency with the
 *    lot's own FROZEN receipt-date rates (recovering the true original
 *    purchase cost), then markup + KDV are applied there. The resulting
 *    base-currency price is stable and equals the stored TradingProduct.price
 *    for the FIFO-current lot — the detail page, storefront, and this export
 *    all agree.
 *  - TODAY's FX snapshot is used ONLY in the forced-currency modes, to express
 *    that finished base-currency price (and cost) in other display currencies
 *    (e.g. the TL column) — settlement conversion, never cost derivation.
 *
 * For a vendor (dealer) list, a companion "retail" (SRP) column is added so the
 * dealer sees the suggested end-customer price (in the retail currency) next to
 * their own buy price.
 */
import type { ReportTableDoc, ReportColumn, ReportRow } from './report-table-types';
import { TRADING_COMPANY } from './report-company';
import {
  lotSellPriceInBase,
  lotCostInCurrency,
  convertDisplay,
  currentLot,
  type ProductPricing,
  type PriceLot,
} from './price-list';

/** Display label for a currency code (Turkish abbreviations for TRY/GBP). */
const CCY_LABEL: Record<string, string> = { TRY: 'TL', GBP: 'STG', EUR: 'EUR', USD: 'USD' };
const ccyLabel = (c: string) => CCY_LABEL[c] ?? c;

/**
 * Shape returned by `tradingProduct.exportPriceList` (per product).
 * `markupType` is widened to `string | null` because the backend infers the
 * Prisma enum as a plain string; it is narrowed to the MarkupType union in
 * `resolvePricing` before being handed to `lotSellPrice`.
 */
export interface PriceListProduct {
  code: string;
  name: string;
  brandName: string;
  // Inline thumbnail (data URL) — present only when the export requested images.
  imageUrl?: string | null;
  // Retail pricing
  price: number | null;
  currency: string;
  markupType: string | null;
  markupValue: number | null;
  kdvRate: number;
  discountPercent?: number;
  // Vendor (dealer) pricing — independent markup/KDV in its own currency
  vendorPrice: number | null;
  vendorCurrency: string;
  vendorMarkupType: string | null;
  vendorMarkupValue: number | null;
  vendorKdvRate: number;
  vendorDiscountPercent?: number;
  // Each lot carries its reconstructed cost legs + frozen receipt-date FX
  // snapshot (see PriceLot) so the base-currency price is derived exactly
  // like the stored product price.
  lots: PriceLot[];
}

/**
 * Logical, user-toggleable columns exposed as checkboxes in the export dialog.
 * The product name and the sell-price columns are mandatory and not listed;
 * cost columns are governed by the separate `includeCost` option.
 */
export type PriceListColumnKey =
  | 'code'
  | 'brand'
  | 'batch'      // lot mode
  | 'received'   // lot mode
  | 'qty'        // lot mode
  | 'totalQty'   // product mode
  | 'lotQty'     // product mode
  | 'retail'     // vendor lists: the companion SRP column(s)
  | 'lineValue'; // lot mode: line value + subtotal/grand-total rows

/**
 * Map a physical column key to the logical checkbox that controls it.
 * Returns null for always-visible columns (name, sell price, cost, image).
 */
function logicalKeyOf(colKey: string): PriceListColumnKey | null {
  if (colKey === 'retailSell' || colKey === 'retailCur' || colKey === 'retailPrice')
    return 'retail';
  if (colKey.startsWith('retail_')) return 'retail';
  switch (colKey) {
    case 'code':
    case 'brand':
    case 'batch':
    case 'received':
    case 'qty':
    case 'totalQty':
    case 'lotQty':
    case 'lineValue':
      return colKey;
    default:
      return null;
  }
}

export interface BuildPriceListOptions {
  mode: 'lot' | 'product';
  includeCost: boolean;
  // Target currencies to render the sell price in, using the current FX snapshot.
  // Empty/undefined keeps the single-(native)-currency behaviour.
  currencies?: string[];
  // Whether the sell price includes KDV. Defaults to true (KDV-inclusive),
  // matching the product detail page and quote/order prices. Cost columns are
  // always KDV-exclusive (KDV on purchases is recoverable, not inventory cost).
  withKdv?: boolean;
  // Which markup to price from: 'retail' (default) or 'vendor' (dealer). Each
  // uses that side's own markup, KDV, and base currency. A 'vendor' list also
  // renders a companion retail (SRP) column.
  priceType?: 'retail' | 'vendor';
  // Current FX snapshot ({ccy: TRY per 1 ccy}) used for ALL conversion. TRY = 1.
  fxRates?: Record<string, number>;
  // Rate basis shown on the document (date + source), for transparency.
  fxDate?: string | Date;
  fxSource?: string | null;
  // True when the applied rate is NOT today's (live fetch fell back to a stored
  // rate) — the document flags this prominently instead of claiming "güncel".
  fxStale?: boolean;
  // Inline thumbnails (PDF only). Each product's imageUrl is a data URL.
  includeImages?: boolean;
  imagesIncluded?: number;
  imagesTotal?: number;
  // Logical columns to render (export-dialog checkboxes). Undefined = all.
  // Mandatory columns (name, sell price, cost when requested) always render.
  visibleColumns?: PriceListColumnKey[];
}

/** dd.MM.yyyy — matches the Turkish date format used across trading exports. */
function formatDay(value: string | Date): string {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()}`;
}

type PriceType = 'retail' | 'vendor';

interface ResolvedPricing {
  pricing: ProductPricing;
  baseCurrency: string;
}

/** Select the retail or vendor markup config plus its base currency. */
function resolvePricing(p: PriceListProduct, priceType: PriceType): ResolvedPricing {
  if (priceType === 'vendor') {
    const mt =
      p.vendorMarkupType === 'FLAT' || p.vendorMarkupType === 'PERCENTAGE'
        ? p.vendorMarkupType
        : null;
    return {
      baseCurrency: p.vendorCurrency,
      pricing: {
        markupType: mt,
        markupValue: p.vendorMarkupValue,
        kdvRate: p.vendorKdvRate,
        discountPercent: p.vendorDiscountPercent,
        fallbackPrice: p.vendorPrice,
      },
    };
  }
  const mt = p.markupType === 'FLAT' || p.markupType === 'PERCENTAGE' ? p.markupType : null;
  return {
    baseCurrency: p.currency,
    pricing: {
      markupType: mt,
      markupValue: p.markupValue,
      kdvRate: p.kdvRate,
      discountPercent: p.discountPercent,
      fallbackPrice: p.price,
    },
  };
}

/**
 * A side's sell price in its OWN base currency, derived from the lot's frozen
 * cost legs (no live FX involved). Returns null when the lot's frozen snapshot
 * is missing a required rate. Used by the native (non-forced-currency) modes
 * so each side is priced and labelled in the currency its markup is actually
 * denominated in — vendor and retail can legitimately differ.
 */
function sellInOwnCurrency(
  lot: PriceLot,
  resolved: ResolvedPricing,
  withKdv: boolean
): number | null {
  return lotSellPriceInBase(lot, resolved.pricing, resolved.baseCurrency, { withKdv });
}

function sharedHeader(
  opts: BuildPriceListOptions
): Pick<ReportTableDoc, 'company' | 'title' | 'params' | 'generatedLabel'> {
  const usesCurrency = (opts.currencies?.length ?? 0) > 0;
  const params: string[] = [
    'Liste: ' + (opts.priceType === 'vendor' ? 'Bayi' : 'Perakende'),
    'Mod: ' + (opts.mode === 'lot' ? 'Parti bazlı' : 'Ürün bazlı'),
    'Fiyat: ' + (opts.includeCost ? 'Maliyet + satış' : 'Sadece satış'),
    'KDV: ' + (opts.withKdv === false ? 'Hariç' : 'Dahil'),
  ];
  if (usesCurrency && opts.currencies) {
    params.push('Para birimi: ' + opts.currencies.map(ccyLabel).join(', '));
  }
  // Rate basis, per column. Base-currency prices are frozen: each lot's cost
  // is converted at its own receipt-date (purchase-time) rate, so they never
  // drift with the market. Today's rate applies ONLY to the forced-currency
  // display columns; when the live fetch failed and a stored rate was used
  // for those columns, say so loudly rather than implying today's rate.
  params.push('Fiyat esası: alım tarihli kur (maliyet sabit, ürün para biriminde)');
  if (usesCurrency && opts.fxDate) {
    const src = opts.fxSource ? ` (${opts.fxSource})` : '';
    const basis = opts.fxStale
      ? 'son kayıtlı kur — CANLI KUR ALINAMADI'
      : 'güncel kur';
    params.push(`Çevrim kuru: ${formatDay(opts.fxDate)} ${basis}${src}`);
  }
  if (opts.priceType === 'vendor') {
    params.push('Perakende sütunu: tavsiye edilen satış fiyatı (SRP)');
  }
  if (opts.includeImages && opts.imagesTotal != null) {
    params.push(`Görseller: ${opts.imagesIncluded ?? 0} / ${opts.imagesTotal} ürün`);
  }
  return {
    company: TRADING_COMPANY,
    title: 'FİYAT LİSTESİ',
    generatedLabel: `Oluşturulma: ${new Date().toLocaleString('tr-TR')}`,
    params,
  };
}

/** The image column (data-URL thumbnail), prepended when images are requested. */
const IMAGE_COLUMN: ReportColumn = { key: 'image', header: 'Görsel', image: true, width: 8 };

// ── Native (single-currency) — lot mode ───────────────────────────────────────

function buildLotColumns(includeCost: boolean, includeRetail: boolean, includeImages: boolean): ReportColumn[] {
  const cols: ReportColumn[] = [];
  if (includeImages) cols.push(IMAGE_COLUMN);
  cols.push(
    { key: 'code', header: 'Kod', width: 18 },
    { key: 'name', header: 'Ürün', width: 28 },
    { key: 'brand', header: 'Marka', width: 16 },
    { key: 'batch', header: 'Parti No', width: 16 },
    { key: 'received', header: 'Giriş Tarihi', width: 14 },
    { key: 'qty', header: 'Mevcut Mik.', num: true }
  );
  if (includeCost) {
    cols.push({ key: 'unitCost', header: 'Birim Maliyet', num: true });
    cols.push({ key: 'costCur', header: 'Maliyet Pb', width: 11 });
  }
  cols.push({ key: 'unitSell', header: includeRetail ? 'Bayi Satış' : 'Birim Satış', num: true });
  cols.push({ key: 'sellCur', header: includeRetail ? 'Bayi Pb' : 'Satış Pb', width: 11 });
  if (includeRetail) {
    cols.push({ key: 'retailSell', header: 'Perakende Satış', num: true });
    cols.push({ key: 'retailCur', header: 'Perakende Pb', width: 12 });
  }
  cols.push({ key: 'lineValue', header: 'Satır Değeri', num: true });
  return cols;
}

function buildLotRows(
  products: PriceListProduct[],
  includeCost: boolean,
  withKdv: boolean,
  priceType: PriceType,
  includeImages: boolean
): ReportRow[] {
  const rows: ReportRow[] = [];
  const includeRetail = priceType === 'vendor';
  // Line values are denominated in each product's own base currency, so the
  // grand total must be kept per currency — a single cross-currency sum would
  // silently add EUR to TL.
  const grandTotals = new Map<string, number>();

  for (const p of products) {
    if (p.lots.length === 0) continue;
    const resolved = resolvePricing(p, priceType);
    const retail = includeRetail ? resolvePricing(p, 'retail') : null;

    // Product group header — carries the brand (fixes the previously-empty Marka
    // column in lot mode) and the inline thumbnail.
    rows.push({
      emphasis: 'group',
      cells: {
        code: p.code,
        name: p.name,
        brand: p.brandName,
        ...(includeImages ? { image: p.imageUrl ?? '' } : {}),
      },
    });

    let productTotal = 0;
    for (const lot of p.lots) {
      // Price in the side's own base currency from the lot's FROZEN cost legs
      // (vendor and retail may be denominated in different currencies).
      const sell = sellInOwnCurrency(lot, resolved, withKdv);
      const lineValue = sell == null ? null : lot.availableQty * sell;
      if (lineValue != null) productTotal += lineValue;

      const cells: ReportRow['cells'] = {
        batch: lot.batchNumber,
        received: formatDay(lot.receivedDate),
        qty: lot.availableQty,
        unitSell: sell == null ? '' : sell,
        sellCur: resolved.baseCurrency,
        lineValue: lineValue == null ? '' : lineValue,
      };
      if (includeCost) {
        const cost = lotCostInCurrency(lot, resolved.baseCurrency);
        cells.unitCost = cost == null ? '' : cost;
        cells.costCur = resolved.baseCurrency;
      }
      if (retail) {
        const r = sellInOwnCurrency(lot, retail, withKdv);
        cells.retailSell = r == null ? '' : r;
        cells.retailCur = retail.baseCurrency;
      }
      rows.push({ cells });
    }

    rows.push({
      emphasis: 'subtotal',
      cells: { name: 'Ara Toplam', sellCur: resolved.baseCurrency, lineValue: productTotal },
    });
    grandTotals.set(
      resolved.baseCurrency,
      (grandTotals.get(resolved.baseCurrency) ?? 0) + productTotal
    );
  }

  if (rows.length > 0) {
    const totals = [...grandTotals.entries()];
    for (const [ccy, total] of totals) {
      rows.push({
        emphasis: 'total',
        cells: {
          name: totals.length > 1 ? `GENEL TOPLAM (${ccyLabel(ccy)})` : 'GENEL TOPLAM',
          sellCur: ccy,
          lineValue: total,
        },
      });
    }
  }

  return rows;
}

// ── Native (single-currency) — product mode ───────────────────────────────────

function buildProductColumns(includeCost: boolean, includeRetail: boolean, includeImages: boolean): ReportColumn[] {
  const cols: ReportColumn[] = [];
  if (includeImages) cols.push(IMAGE_COLUMN);
  cols.push(
    { key: 'code', header: 'Kod', width: 18 },
    { key: 'name', header: 'Ürün', width: 32 },
    { key: 'brand', header: 'Marka', width: 18 },
    { key: 'totalQty', header: 'Toplam Mevcut', num: true },
    { key: 'lotQty', header: 'Bu Fiyatta Mik.', num: true }
  );
  if (includeCost) cols.push({ key: 'unitCost', header: 'Güncel Maliyet', num: true });
  cols.push({ key: 'sellPrice', header: includeRetail ? 'Bayi Satış' : 'Satış Fiyatı', num: true });
  if (includeRetail) cols.push({ key: 'retailPrice', header: 'Perakende Satış', num: true });
  cols.push({ key: 'cur', header: includeRetail ? 'Bayi Pb' : 'Pb', width: 10 });
  if (includeRetail) cols.push({ key: 'retailCur', header: 'Perakende Pb', width: 12 });
  return cols;
}

function buildProductRows(
  products: PriceListProduct[],
  includeCost: boolean,
  withKdv: boolean,
  priceType: PriceType,
  includeImages: boolean
): ReportRow[] {
  const rows: ReportRow[] = [];
  const includeRetail = priceType === 'vendor';
  let count = 0;

  for (const p of products) {
    const cur: PriceLot | null = currentLot(p.lots);
    if (!cur) continue;
    const resolved = resolvePricing(p, priceType);
    const retail = includeRetail ? resolvePricing(p, 'retail') : null;
    const totalQty = p.lots.reduce((sum, l) => sum + l.availableQty, 0);
    // Price in the side's own base currency from the FIFO lot's frozen cost —
    // for the current lot this equals the stored product price.
    const sellPrice = sellInOwnCurrency(cur, resolved, withKdv);

    const cells: ReportRow['cells'] = {
      code: p.code,
      name: p.name,
      brand: p.brandName,
      totalQty,
      lotQty: cur.availableQty,
      sellPrice: sellPrice == null ? '' : sellPrice,
      cur: resolved.baseCurrency,
    };
    if (includeImages) cells.image = p.imageUrl ?? '';
    if (includeCost) {
      const cost = lotCostInCurrency(cur, resolved.baseCurrency);
      cells.unitCost = cost == null ? '' : cost;
    }
    if (retail) {
      const r = sellInOwnCurrency(cur, retail, withKdv);
      cells.retailPrice = r == null ? '' : r;
      cells.retailCur = retail.baseCurrency;
    }
    rows.push({ cells });
    count++;
  }

  if (count > 0) {
    rows.push({
      emphasis: 'total',
      cells: { code: `${count} ürün` },
    });
  }

  return rows;
}

// ── Multi-currency variants ───────────────────────────────────────────────────
// Each selected currency gets a cost (when requested), a (vendor/native) sell,
// and — for a vendor list — a retail (SRP) column. The figure is FIRST derived
// in the side's own base currency from the lot's frozen cost legs, THEN the
// finished amount is expressed in the selected display currency with the
// CURRENT FX snapshot. A blank cell means a required rate is missing.

function ccyPriceColumns(
  leading: ReportColumn[],
  currencies: string[],
  includeCost: boolean,
  includeRetail: boolean
): ReportColumn[] {
  const cols = [...leading];
  for (const c of currencies) {
    if (includeCost) cols.push({ key: `cost_${c}`, header: `Maliyet (${ccyLabel(c)})`, num: true });
    cols.push({
      key: `sell_${c}`,
      header: includeRetail ? `Bayi (${ccyLabel(c)})` : `Satış (${ccyLabel(c)})`,
      num: true,
    });
    if (includeRetail) cols.push({ key: `retail_${c}`, header: `Perakende (${ccyLabel(c)})`, num: true });
  }
  return cols;
}

function ccyPriceCells(
  cells: ReportRow['cells'],
  lot: PriceLot,
  pricing: ResolvedPricing,
  currencies: string[],
  includeCost: boolean,
  withKdv: boolean,
  fx: Record<string, number>,
  retail: ResolvedPricing | null
): void {
  // Frozen leg once per lot: cost and sell in the side's own base currency.
  const costBase = lotCostInCurrency(lot, pricing.baseCurrency);
  const sellBase = sellInOwnCurrency(lot, pricing, withKdv);
  const retailBase = retail ? sellInOwnCurrency(lot, retail, withKdv) : null;
  for (const c of currencies) {
    if (includeCost) {
      const cost = costBase == null ? null : convertDisplay(costBase, pricing.baseCurrency, c, fx);
      cells[`cost_${c}`] = cost == null ? '' : cost;
    }
    const sell = sellBase == null ? null : convertDisplay(sellBase, pricing.baseCurrency, c, fx);
    cells[`sell_${c}`] = sell == null ? '' : sell;
    if (retail) {
      const r =
        retailBase == null ? null : convertDisplay(retailBase, retail.baseCurrency, c, fx);
      cells[`retail_${c}`] = r == null ? '' : r;
    }
  }
}

function buildLotRowsCcy(
  products: PriceListProduct[],
  currencies: string[],
  includeCost: boolean,
  withKdv: boolean,
  priceType: PriceType,
  fx: Record<string, number>,
  includeImages: boolean
): ReportRow[] {
  const rows: ReportRow[] = [];
  const includeRetail = priceType === 'vendor';
  for (const p of products) {
    if (p.lots.length === 0) continue;
    const pricing = resolvePricing(p, priceType);
    const retail = includeRetail ? resolvePricing(p, 'retail') : null;
    rows.push({
      emphasis: 'group',
      cells: {
        code: p.code,
        name: p.name,
        brand: p.brandName,
        ...(includeImages ? { image: p.imageUrl ?? '' } : {}),
      },
    });
    for (const lot of p.lots) {
      const cells: ReportRow['cells'] = {
        batch: lot.batchNumber,
        received: formatDay(lot.receivedDate),
        qty: lot.availableQty,
      };
      ccyPriceCells(cells, lot, pricing, currencies, includeCost, withKdv, fx, retail);
      rows.push({ cells });
    }
  }
  return rows;
}

function buildProductRowsCcy(
  products: PriceListProduct[],
  currencies: string[],
  includeCost: boolean,
  withKdv: boolean,
  priceType: PriceType,
  fx: Record<string, number>,
  includeImages: boolean
): ReportRow[] {
  const rows: ReportRow[] = [];
  const includeRetail = priceType === 'vendor';
  let count = 0;
  for (const p of products) {
    const cur = currentLot(p.lots);
    if (!cur) continue;
    const pricing = resolvePricing(p, priceType);
    const retail = includeRetail ? resolvePricing(p, 'retail') : null;
    const totalQty = p.lots.reduce((sum, l) => sum + l.availableQty, 0);
    const cells: ReportRow['cells'] = {
      code: p.code,
      name: p.name,
      brand: p.brandName,
      totalQty,
      lotQty: cur.availableQty,
    };
    if (includeImages) cells.image = p.imageUrl ?? '';
    ccyPriceCells(cells, cur, pricing, currencies, includeCost, withKdv, fx, retail);
    rows.push({ cells });
    count++;
  }
  if (count > 0) rows.push({ emphasis: 'total', cells: { code: `${count} ürün` } });
  return rows;
}

/**
 * Apply the dialog's column selection to a finished doc and stamp the
 * sectioned PDF layout. Rows that only carry a hidden column's data degrade
 * with it: without `lineValue` the lot-mode subtotal/total rows are dropped
 * (they would render as empty label rows), and the product-mode count label
 * relocates from the hidden `code` column to the always-visible name column.
 */
function finalizeDoc(
  doc: ReportTableDoc,
  mode: 'lot' | 'product',
  visibleColumns?: PriceListColumnKey[]
): ReportTableDoc {
  const withLayout: ReportTableDoc = { ...doc, layout: 'sections' };
  if (!visibleColumns) return withLayout;
  const visible = new Set(visibleColumns);

  const columns = withLayout.columns.filter((c) => {
    const logical = logicalKeyOf(c.key);
    return logical == null || visible.has(logical);
  });

  let rows = withLayout.rows;
  if (mode === 'lot' && !visible.has('lineValue')) {
    rows = rows.filter((r) => r.emphasis !== 'subtotal' && r.emphasis !== 'total');
  }
  if (mode === 'product' && !visible.has('code')) {
    rows = rows.map((r) =>
      r.emphasis === 'total' && r.cells.code != null
        ? { ...r, cells: { ...r.cells, code: undefined, name: r.cells.code } }
        : r
    );
  }

  return { ...withLayout, columns, rows };
}

export function buildPriceListDoc(
  products: PriceListProduct[],
  opts: BuildPriceListOptions
): ReportTableDoc {
  const head = sharedHeader(opts);
  const currencies = opts.currencies ?? [];
  const withKdv = opts.withKdv ?? true;
  const priceType = opts.priceType ?? 'retail';
  const includeImages = opts.includeImages ?? false;
  const includeRetail = priceType === 'vendor';
  const fx = opts.fxRates ?? { TRY: 1 };

  if (opts.mode === 'lot') {
    if (currencies.length > 0) {
      const leading: ReportColumn[] = [];
      if (includeImages) leading.push(IMAGE_COLUMN);
      leading.push(
        { key: 'code', header: 'Kod', width: 18 },
        { key: 'name', header: 'Ürün', width: 26 },
        { key: 'brand', header: 'Marka', width: 14 },
        { key: 'batch', header: 'Parti No', width: 14 },
        { key: 'received', header: 'Giriş Tarihi', width: 13 },
        { key: 'qty', header: 'Mevcut Mik.', num: true }
      );
      return finalizeDoc(
        {
          ...head,
          columns: ccyPriceColumns(leading, currencies, opts.includeCost, includeRetail),
          rows: buildLotRowsCcy(products, currencies, opts.includeCost, withKdv, priceType, fx, includeImages),
        },
        'lot',
        opts.visibleColumns
      );
    }
    return finalizeDoc(
      {
        ...head,
        columns: buildLotColumns(opts.includeCost, includeRetail, includeImages),
        rows: buildLotRows(products, opts.includeCost, withKdv, priceType, includeImages),
      },
      'lot',
      opts.visibleColumns
    );
  }

  if (currencies.length > 0) {
    const leading: ReportColumn[] = [];
    if (includeImages) leading.push(IMAGE_COLUMN);
    leading.push(
      { key: 'code', header: 'Kod', width: 18 },
      { key: 'name', header: 'Ürün', width: 30 },
      { key: 'brand', header: 'Marka', width: 16 },
      { key: 'totalQty', header: 'Toplam Mevcut', num: true },
      { key: 'lotQty', header: 'Bu Fiyatta Mik.', num: true }
    );
    return finalizeDoc(
      {
        ...head,
        columns: ccyPriceColumns(leading, currencies, opts.includeCost, includeRetail),
        rows: buildProductRowsCcy(products, currencies, opts.includeCost, withKdv, priceType, fx, includeImages),
      },
      'product',
      opts.visibleColumns
    );
  }

  return finalizeDoc(
    {
      ...head,
      columns: buildProductColumns(opts.includeCost, includeRetail, includeImages),
      rows: buildProductRows(products, opts.includeCost, withKdv, priceType, includeImages),
    },
    'product',
    opts.visibleColumns
  );
}
